import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, User, Menu, X, ShieldCheck, ShieldOff, ChevronDown } from "lucide-react";
import ReviewerHeaderBadge from "@/components/ReviewerHeaderBadge";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function Header() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [open]);

  const link = (to, label, testid, mobile = false) => (
    <Link
      to={to}
      data-testid={testid}
      className={mobile
        ? `block w-full text-base font-medium px-4 py-3 border-b border-zinc-100 transition-colors ${
            loc.pathname === to ? "text-[#D42B1E] bg-[#D42B1E]/5" : "text-zinc-800 hover:bg-zinc-50"
          }`
        : `text-sm font-medium tracking-tight px-3 py-2 rounded-sm transition-colors ${
            loc.pathname === to ? "text-[#D42B1E]" : "text-zinc-700 hover:text-zinc-900"
          }`
      }
    >
      {label}
    </Link>
  );

  // Pythagore admins are internal staff: they only need Revues + Admin (and
  // Contact, kept as a public/partner-facing page). Partner-side tools
  // (Configurateur / Catalogue / Liste de prix / Mes devis) are hidden for
  // them — see also the AdminGuard route filter in App.js.
  const navItems = (mobile = false) => {
    // Super Admins (admin + reviewer) are first-class internal users: they
    // need access to BOTH the admin back-office AND every partner-side tool
    // (build BOMs, browse catalogue, manage quotes…). They fall through to
    // the default partner navigation below; we just prepend the internal
    // shortcuts.
    const isSuperAdmin = user?.is_admin && user?.is_fortinet_reviewer;
    if (isSuperAdmin) {
      return (
        <>
          {link("/admin",          "Admin",                  "header-nav-admin",         mobile)}
          {link("/reviews",        "Revues",                 "header-nav-reviews",       mobile)}
          {link("/configurator",   "Générateur de BOM/BOQ",  "header-nav-configurator",  mobile)}
          {link("/catalog",        "Catalogue",              "header-nav-catalog",       mobile)}
          {link("/pricelist",      "Liste de prix",          "header-nav-pricelist",     mobile)}
          {link("/quotes",         "Mes devis",              "header-nav-quotes",        mobile)}
          {link("/quotes/validated","Devis validés",         "header-nav-validated",     mobile)}
        </>
      );
    }
    if (user?.is_admin) {
      return (
        <>
          {link("/reviews", "Revues", "header-nav-reviews", mobile)}
          {link("/admin", "Admin", "header-nav-admin", mobile)}
        </>
      );
    }
    // Fortinet Reviewers (non-admin) are commercial relays: their entire
    // workspace lives under /reviews — the inbound queue + the inline BOM
    // editor. All partner-side pages stay hidden (see PartnerStrictRoute).
    if (user?.is_fortinet_reviewer) {
      return (
        <>
          {link("/reviews", "Revues", "header-nav-reviews", mobile)}
        </>
      );
    }
    return (
      <>
        {user && link("/configurator", "Générateur de BOM/BOQ", "header-nav-configurator", mobile)}
        {user && link("/catalog", "Catalogue", "header-nav-catalog", mobile)}
        {user && link("/pricelist", "Liste de prix", "header-nav-pricelist", mobile)}
        {user && link("/quotes", "Mes devis", "header-nav-quotes", mobile)}
        {user && link("/quotes/validated", "Devis validés", "header-nav-validated", mobile)}
      </>
    );
  };

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-zinc-200"
    >
      <div className="max-w-[1600px] mx-auto px-3 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-2">
        <Link to="/" data-testid="header-home-link" className="flex items-center gap-3 shrink-0">
          <img
            src="/pythagore-logo.svg"
            alt="Pythagore"
            className="h-6 sm:h-7 w-auto"
          />
          <span className="text-[10px] sm:text-xs font-semibold tracking-[0.2em] text-zinc-500 uppercase hidden lg:inline border-l border-zinc-200 pl-3">
            BOM Engine
          </span>
        </Link>

        {/* Desktop nav — visible from lg breakpoint up */}
        <nav className="hidden lg:flex items-center gap-1" data-testid="header-nav-desktop">
          {navItems(false)}
        </nav>

        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          <ReviewerHeaderBadge />
          {/* Always-visible right-side links — Manuel & Contact stay reachable
              for both authenticated partners and public visitors. */}
          <Button variant="ghost" size="sm" asChild className="rounded-sm hidden sm:inline-flex" data-testid="header-manual-button">
            <Link to="/manual">Manuel</Link>
          </Button>
          <Button size="sm" asChild className="bg-[#D42B1E] hover:bg-[#B32218] rounded-sm hidden sm:inline-flex" data-testid="header-contact-button">
            <Link to="/contact">Contact</Link>
          </Button>
          {user ? (
            <div className="flex items-center gap-1 sm:gap-2">
              {/* User dropdown — surfaces profile link + 2FA status + logout */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-zinc-100 transition-colors text-xs"
                    data-testid="header-user-menu-trigger"
                    title="Profil et sécurité"
                  >
                    <span className="h-7 w-7 rounded-full bg-[#D42B1E]/10 border border-[#D42B1E]/20 text-[#D42B1E] inline-flex items-center justify-center font-semibold text-[11px]">
                      {(user.full_name || user.email || "?").trim().charAt(0).toUpperCase()}
                    </span>
                    <span className="hidden md:flex flex-col items-start leading-tight max-w-[160px]">
                      <span className="font-medium text-zinc-900 truncate" data-testid="header-user-name">
                        {user.full_name}
                      </span>
                      <span className="text-[10px] text-zinc-500 truncate flex items-center gap-1">
                        {user.totp_enabled ? (
                          <>
                            <ShieldCheck className="h-2.5 w-2.5 text-emerald-600" />
                            2FA active
                          </>
                        ) : (
                          <>
                            <ShieldOff className="h-2.5 w-2.5 text-zinc-400" />
                            2FA inactive
                          </>
                        )}
                      </span>
                    </span>
                    <ChevronDown className="h-3.5 w-3.5 text-zinc-400 hidden md:block" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72" data-testid="header-user-menu">
                  <DropdownMenuLabel className="pb-2">
                    <p className="text-sm font-semibold text-zinc-900 truncate">{user.full_name}</p>
                    <p className="text-[11px] font-mono text-zinc-500 truncate">{user.email}</p>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {/* 2FA status block — clickable shortcut to /profile */}
                  <Link to="/profile" data-testid="header-user-menu-2fa">
                    <DropdownMenuItem className="cursor-pointer flex items-start gap-2 py-2.5">
                      {user.totp_enabled ? (
                        <ShieldCheck className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                      ) : (
                        <ShieldOff className="h-4 w-4 text-zinc-400 mt-0.5 shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-zinc-900">
                            Authentification 2FA
                          </span>
                          <span
                            className={`text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm ${
                              user.totp_enabled
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-zinc-100 text-zinc-600"
                            }`}
                          >
                            {user.totp_enabled ? "Activée" : "Inactive"}
                          </span>
                        </div>
                        <p className="text-[11px] text-zinc-500 mt-0.5">
                          {user.totp_enabled
                            ? "Code 6 chiffres exigé à chaque connexion."
                            : "Activer pour exiger un code 6 chiffres au login."}
                        </p>
                        <p className="text-[10px] text-[#D42B1E] mt-1 font-medium">
                          {user.totp_enabled ? "Gérer →" : "Configurer →"}
                        </p>
                      </div>
                    </DropdownMenuItem>
                  </Link>
                  <DropdownMenuSeparator />
                  <Link to="/profile" data-testid="header-user-menu-profile">
                    <DropdownMenuItem className="cursor-pointer">
                      <User className="h-3.5 w-3.5 mr-2 text-zinc-500" />
                      Profil et sécurité
                    </DropdownMenuItem>
                  </Link>
                  <DropdownMenuItem
                    className="cursor-pointer text-rose-700 focus:text-rose-800"
                    onClick={() => { logout(); nav("/"); }}
                    data-testid="header-user-menu-logout"
                  >
                    <LogOut className="h-3.5 w-3.5 mr-2" />
                    Se déconnecter
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {/* Hamburger — visible below lg breakpoint */}
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
                data-testid="header-mobile-menu-toggle"
                className="lg:hidden h-9 w-9 inline-flex items-center justify-center rounded-sm text-zinc-700 hover:bg-zinc-100"
              >
                {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          ) : (
            <>
              {/* Mobile hamburger for unauthenticated visitors */}
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
                data-testid="header-mobile-menu-toggle-guest"
                className="lg:hidden h-9 w-9 inline-flex items-center justify-center rounded-sm text-zinc-700 hover:bg-zinc-100"
              >
                {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 top-14 z-40" data-testid="header-mobile-drawer">
          <button
            type="button"
            aria-label="Fermer"
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-zinc-900/40"
          />
          <nav className="relative bg-white border-b border-zinc-200 shadow-lg max-h-[calc(100vh-3.5rem)] overflow-y-auto">
            {user && (
              <div className="px-4 py-3 border-b border-zinc-100 bg-zinc-50/50 flex items-center gap-2">
                <User className="h-4 w-4 text-zinc-500" />
                <span className="text-sm font-medium text-zinc-800">{user.full_name}</span>
                {user.is_admin && (
                  <span className="ml-auto text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-sm bg-[#D42B1E] text-white">Admin</span>
                )}
                {user.is_fortinet_reviewer && !user.is_admin && (
                  <span className="ml-auto text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-sm bg-[#D42B1E]/10 text-[#D42B1E] border border-[#D42B1E]/30">Reviewer</span>
                )}
              </div>
            )}
            {navItems(true)}
            {user && (
              <Link
                to="/profile"
                onClick={() => setOpen(false)}
                className="block px-4 py-3 border-t border-zinc-100 text-sm text-zinc-700 hover:bg-zinc-50 hover:text-[#D42B1E]"
                data-testid="header-profile-mobile-link"
              >
                Profil et sécurité
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
